import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, MapPin, Bell, Cloud, Sun, CloudRain, Snowflake, ArrowRight } from "lucide-react";

/* Personalized traveler dashboard for the /msp landing.
   Shows: current city + live weather (Open-Meteo, no key), next trip preview
   (from /api/travel_groups if signed in, else a warm CTA), and unread alerts. */

const CITY_COORDS = {
  Portland:  { lat: 45.5152, lon: -122.6784 },
  "New York":{ lat: 40.7128, lon:  -74.0060 },
  Chicago:   { lat: 41.8781, lon:  -87.6298 },
  London:    { lat: 51.5074, lon:   -0.1278 },
  Bengaluru: { lat: 12.9716, lon:   77.5946 },
  Tokyo:     { lat: 35.6762, lon:  139.6503 },
};

function useCity() {
  const [city, setCity] = useState({ name: "Portland", region: "Oregon", country: "USA" });
  useEffect(() => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
      const map = {
        "America/Los_Angeles": { name: "Portland", region: "Oregon", country: "USA" },
        "America/New_York":    { name: "New York", region: "New York", country: "USA" },
        "America/Chicago":     { name: "Chicago",  region: "Illinois", country: "USA" },
        "Europe/London":       { name: "London",   region: "England",  country: "UK"  },
        "Asia/Kolkata":        { name: "Bengaluru",region: "Karnataka",country: "India" },
        "Asia/Tokyo":          { name: "Tokyo",    region: "Kanto",    country: "Japan" },
      };
      if (map[tz]) setCity(map[tz]);
    } catch { /* silent */ }
  }, []);
  return city;
}

function useWeather(cityName) {
  const [w, setW] = useState(null);
  useEffect(() => {
    const c = CITY_COORDS[cityName];
    if (!c) return;
    const url = `https://api.open-meteo.com/v1/forecast?latitude=${c.lat}&longitude=${c.lon}&current=temperature_2m,weather_code&temperature_unit=fahrenheit`;
    fetch(url).then((r) => r.json()).then((j) => {
      if (j?.current) setW({ temp: Math.round(j.current.temperature_2m), code: j.current.weather_code });
    }).catch(() => { /* offline safe */ });
  }, [cityName]);
  return w;
}

function WeatherIcon({ code }) {
  if (code == null) return <Sun className="w-6 h-6" />;
  if ([61,63,65,66,67,80,81,82].includes(code)) return <CloudRain className="w-6 h-6" />;
  if ([71,73,75,77,85,86].includes(code)) return <Snowflake className="w-6 h-6" />;
  if ([1,2,3,45,48].includes(code)) return <Cloud className="w-6 h-6" />;
  return <Sun className="w-6 h-6" />;
}

function useNextTrip() {
  const [trip, setTrip] = useState(null);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/travel_groups`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => {
        const groups = j?.items || j || [];
        const upcoming = Array.isArray(groups) ? groups.find((g) => g?.start_date || g?.trip_start) : null;
        if (upcoming) {
          setTrip({
            title: upcoming.name || upcoming.title || "Your next trip",
            when: upcoming.start_date || upcoming.trip_start,
            id: upcoming.id || upcoming._id,
          });
        }
      }).catch(() => { /* signed out or offline */ });
  }, []);
  return trip;
}

function useUnread() {
  const [n, setN] = useState(0);
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    if (!API) return;
    fetch(`${API}/api/notifications/unread-count`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (typeof j?.count === "number") setN(j.count); })
      .catch(() => {});
  }, []);
  return n;
}

export default function PersonalizeWidget() {
  const city = useCity();
  const weather = useWeather(city.name);
  const trip = useNextTrip();
  const unread = useUnread();

  return (
    <section className="max-w-6xl mx-auto px-5 lg:px-8 -mt-4 pb-2" data-testid="msp-personalize">
      <div className="rounded-3xl bg-white/85 backdrop-blur border border-white shadow-xl grid sm:grid-cols-3 gap-0 sm:gap-0 overflow-hidden">
        {/* Current city + weather */}
        <div className="p-5 sm:border-r sm:border-slate-100 flex items-center gap-4" data-testid="msp-personalize-city">
          <span className="w-12 h-12 rounded-2xl bg-sky-50 text-sky-600 grid place-items-center shrink-0">
            <MapPin className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">You're in</div>
            <div className="font-serif font-800 text-lg text-blue-900 leading-tight truncate">{city.name}, {city.region}</div>
            <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
              <WeatherIcon code={weather?.code} />
              {weather ? `${weather.temp}°F today` : `${city.country} · loading weather…`}
            </div>
          </div>
        </div>

        {/* Next trip */}
        <div className="p-5 border-t sm:border-t-0 sm:border-r border-slate-100 flex items-center gap-4" data-testid="msp-personalize-trip">
          <span className="w-12 h-12 rounded-2xl bg-amber-50 text-amber-600 grid place-items-center shrink-0">
            <Calendar className="w-5 h-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-amber-700">Your next trip</div>
            {trip ? (
              <>
                <div className="font-serif font-800 text-lg text-blue-900 leading-tight truncate">{trip.title}</div>
                <Link to={`/trips/${trip.id}`} data-testid="msp-personalize-trip-open"
                  className="text-xs text-blue-700 font-700 mt-0.5 inline-flex items-center gap-1 hover:underline">
                  Open trip <ArrowRight className="w-3 h-3" />
                </Link>
              </>
            ) : (
              <>
                <div className="font-serif font-800 text-lg text-blue-900 leading-tight">Plan your first trip</div>
                <Link to="/msp/plan" data-testid="msp-personalize-plan"
                  className="text-xs text-blue-700 font-700 mt-0.5 inline-flex items-center gap-1 hover:underline">
                  Start planning <ArrowRight className="w-3 h-3" />
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Unread alerts */}
        <div className="p-5 border-t sm:border-t-0 border-slate-100 flex items-center gap-4" data-testid="msp-personalize-alerts">
          <span className="w-12 h-12 rounded-2xl bg-rose-50 text-rose-600 grid place-items-center shrink-0 relative">
            <Bell className="w-5 h-5" />
            {unread > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-800 grid place-items-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700">Alerts</div>
            {unread > 0 ? (
              <>
                <div className="font-serif font-800 text-lg text-blue-900 leading-tight">{unread} new update{unread === 1 ? "" : "s"}</div>
                <Link to="/notifications" data-testid="msp-personalize-alerts-open"
                  className="text-xs text-blue-700 font-700 mt-0.5 inline-flex items-center gap-1 hover:underline">
                  Review now <ArrowRight className="w-3 h-3" />
                </Link>
              </>
            ) : (
              <>
                <div className="font-serif font-800 text-lg text-blue-900 leading-tight">All caught up</div>
                <div className="text-xs text-slate-500 mt-0.5">No new alerts</div>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
